import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SortIcon from "@mui/icons-material/Sort";

import ConfirmationDialog from "lib/components/dialog/ConfirmationDialog";

import { usePrincipal } from "authentication/AuthProvider";

import SelectExtractBlueprintDialog from "document/SelectExtractBlueprintDialog";

import { createExtract } from "extract/ExtractService";

import CreateTrajectoryDialog from "trajectory/create/CreateTrajectoryDialog";
import TrajectoryActionBar from "trajectory/TrajectoryActionBar";
import {
	getGroupedTrajectories,
	Group,
	Grouped,
	TrajectoryInfo,
	TrajectoryRight
} from "trajectory/TrajectoryService";

import { dateToString, isAfter } from "utils/DateUtils";
import { ERR_INTERNET_DISCONNECTED } from "utils/ErrorCodes";
import { asTitle } from "utils/Utils";

import { Status } from "document/StatusTypes";
import useFetch from "utils/fetch/useFetch";
import TrajectoryClient from "./TrajectoryClient";
import TrajectoryGroup from "./TrajectoryGroup";

const TrajectoryOverview: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const principal = usePrincipal();

	const { call } = useFetch();

	// TODO: Have a real useStorage hook that takes care of the prefix.
	const key = useMemo(
		() =>
			`${principal?.userId}.${principal?.organisationId}.trajectories.groupKey`,
		[principal]
	);

	const [trajectories, setTrajectories] = useState<Grouped<TrajectoryInfo>>({
		grouped: {},
		ungrouped: []
	});

	const [error, setError] = useState<string>();
	const [success, setSuccess] = useState<string>();
	const [filter, setFilter] = useState<string>();
	const [showInactive, setShowInactive] = useState(false);
	const [selected, setSelected] = useState<number[]>([]);

	const [createDialog, setCreateDialog] = useState(false);
	const [deleteDialog, setDeleteDialog] = useState(false);
	const [extractDialog, setExtractDialog] = useState(false);

	const groupByFieldRef = useRef<HTMLInputElement>(null);
	const [groupField, setGroupField] = useState<string | null>(
		localStorage.getItem(key)
	);

	useEffect(() => {
		loadTrajectories();
	}, [groupField]);

	const filtered: Grouped<TrajectoryInfo> = useMemo(() => {
		return {
			grouped: toFilteredGroup(trajectories.grouped),
			ungrouped: toFiltered(trajectories.ungrouped)
		};
	}, [filter, trajectories, showInactive]);

	const filteredSelected: TrajectoryInfo[] = useMemo(() => {
		return Object.values(filtered.grouped)
			.concat(filtered.ungrouped)
			.flat()
			.filter(t => selected.includes(t.id));
	}, [filtered, selected]);

	const amountOfFiltered = useMemo(() => {
		return Object.values(filtered.grouped).reduce(
			(acc, group) => acc + group.length,
			filtered.ungrouped.length
		);
	}, [filtered]);

	function loadTrajectories(): void {
		getGroupedTrajectories(groupField)
			.then(data => setTrajectories(data))
			.catch(() =>
				setError(
					t("loadDocumentError", { document: "trajectory_other" })
				)
			);
	}

	function toFilteredGroup(
		trajectories: Group<TrajectoryInfo>
	): Group<TrajectoryInfo> {
		const result: Group<TrajectoryInfo> = {};
		Object.entries(trajectories).forEach(([key, value]) => {
			result[key] = toFiltered(value);
		});

		return result;
	}

	function toFiltered(trajectories: Array<TrajectoryInfo>): TrajectoryInfo[] {
		let filtered: Array<TrajectoryInfo> = trajectories;

		if (filter && filter.trim() !== "") {
			const lowerCaseFilter = filter.toLowerCase();
			filtered = filtered.filter(
				t =>
					t.name.toLowerCase().indexOf(lowerCaseFilter) !== -1 ||
					t.description.toLowerCase().indexOf(lowerCaseFilter) !== -1
			);
		}

		if (!showInactive) {
			filtered = filtered.filter(
				t => t.activeUntil !== null && isAfter(t.activeUntil, 0)
			);
		}

		return filtered;
	}

	function select(ids: Array<number>, select: boolean): void {
		if (select) {
			setSelected(prev => prev.concat(...ids));
		} else {
			setSelected(prev =>
				prev.filter(value => ids.indexOf(value) === -1)
			);
		}
	}

	function deleteSelected(): Promise<void> {
		return Promise.all(
			filteredSelected.map(t =>
				call(
					TrajectoryClient.updateStatus(t.id, {
						status: Status.DELETED
					})
				)
			)
		)
			.then(() => {
				setSelected([]);
				loadTrajectories();
			})
			.catch((reason: Error) => {
				let errorMessage: string;
				if (reason.message === ERR_INTERNET_DISCONNECTED) {
					errorMessage = t("internetDisconnectedError");
				} else {
					errorMessage = t("deleteDocumentError", {
						document: "trajectory"
					});
				}
				setError(errorMessage);
				setSuccess(undefined);
			})
			.finally(() => setDeleteDialog(false));
	}

	function generateExtracts(
		name: string,
		extract: number,
		asOf: Date | null
	): Promise<void> {
		const request = {
			name: name,
			extract: extract,
			trajectories: selected,
			asOf: asOf ? dateToString(asOf) : undefined
		};

		return createExtract(request)
			.then(() => {
				setSelected([]);
				setSuccess(t("generateExtractSuccess"));
				setError(undefined);
				setExtractDialog(false);
			})
			.catch(() => {
				setSuccess(undefined);
				setError(t("generateExtractError"));
			});
	}

	const toggleShowInactive = useCallback((): void => {
		setShowInactive(previous => !previous);
	}, []);

	const onSelectAll = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			const ids = Object.values(filtered.grouped)
				.concat(filtered.ungrouped)
				.flat()
				.filter(t => t.rights.includes(TrajectoryRight.METADATA))
				.map(t => t.id);
			if (event.target.checked) {
				setSelected(ids);
			} else {
				setSelected([]);
			}
		},
		[filtered]
	);

	function updateGroupField(value: string): void {
		localStorage.setItem(key, value);
		setGroupField(value);
	}

	const onKeyDownGroupField = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>): void => {
			const field = event.target as HTMLInputElement;
			const value = field.value;
			if (event.key === "Enter" && value !== groupField) {
				updateGroupField(value);
			}
		},
		[groupField]
	);

	const onBlurGroupField = useCallback(
		(event: React.FocusEvent<HTMLInputElement>): void => {
			const value = event.target.value;
			if (value !== groupField) {
				updateGroupField(value);
			}
		},
		[groupField]
	);

	const onClickGroupField = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>): void => {
			const value = groupByFieldRef.current?.value;
			if (value && value !== groupField) {
				updateGroupField(value);
			}
		},
		[groupField]
	);

	return (
		<>
			{error && (
				<Alert id="error-message" severity="error">
					{error}
				</Alert>
			)}
			{success && (
				<Alert id="success-message" severity="success">
					{success}
				</Alert>
			)}

			<TrajectoryActionBar
				hasSelection={filteredSelected.length === 0}
				onCreate={(): void => setCreateDialog(true)}
				onDelete={(): void => setDeleteDialog(true)}
				onGenerateExtract={(): void => setExtractDialog(true)}
				onFilter={setFilter}
			/>

			<Grid
				container
				alignItems="flex-end"
				justifyContent="space-between"
				sx={{ paddingBottom: 2 }}>
				<Grid item>
					{groupField && (
						<FormGroup>
							<FormControlLabel
								control={
									<Checkbox
										indeterminate={
											filteredSelected.length > 0 &&
											filteredSelected.length <
												amountOfFiltered
										}
										checked={
											filteredSelected.length > 0 &&
											filteredSelected.length ===
												amountOfFiltered
										}
										onChange={onSelectAll}
									/>
								}
								label={t("selectAll")}
							/>
						</FormGroup>
					)}
				</Grid>

				<Grid item>
					<Grid container alignItems="flex-end">
						<Grid item>
							<FormControl variant="standard">
								<InputLabel htmlFor="group-by-field">
									{t("groupByField")}
								</InputLabel>
								<Input
									id="group-by-field"
									defaultValue={groupField}
									inputProps={{ ref: groupByFieldRef }}
									onKeyDown={onKeyDownGroupField}
									onBlur={onBlurGroupField}
									endAdornment={
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={onClickGroupField}>
												<SortIcon />
											</IconButton>
										</InputAdornment>
									}
								/>
							</FormControl>
						</Grid>

						<Grid item>
							<FormGroup>
								<FormControlLabel
									control={
										<Switch
											checked={showInactive}
											onChange={toggleShowInactive}
										/>
									}
									label={asTitle(t("inactive"))}
									labelPlacement="start"
								/>
							</FormGroup>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			{groupField && (
				<div>
					{Object.entries(filtered.grouped)
						.filter(([_key, value]) => value.length > 0)
						.map(([key, value]) => (
							<Accordion key={key} defaultExpanded={true}>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}>
									<Typography>{key}</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<TrajectoryGroup
										trajectories={value}
										selected={selected}
										onSelect={select}
									/>
								</AccordionDetails>
							</Accordion>
						))}

					{filtered.ungrouped.length > 0 && (
						<Accordion defaultExpanded={true}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography>
									{asTitle(
										t("uncatogorized", {
											document: "trajectory"
										})
									)}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<TrajectoryGroup
									trajectories={filtered.ungrouped}
									selected={selected}
									onSelect={select}
								/>
							</AccordionDetails>
						</Accordion>
					)}
				</div>
			)}

			{!groupField && (
				<TrajectoryGroup
					trajectories={filtered.ungrouped}
					selected={selected}
					onSelect={select}
				/>
			)}

			{createDialog && (
				<CreateTrajectoryDialog
					onClose={(): void => setCreateDialog(false)}
					onConfirm={(id) => navigate("/trajectory/" + id)}
				/>
			)}

			{deleteDialog && (
				<ConfirmationDialog
					title={t("deleteDocumentTitle", {
						document: "trajectory",
						count: filteredSelected.length
					})}
					dialogText={t("deleteDocumentMessage", {
						document: "trajectory",
						count: filteredSelected.length
					})}
					cancelLabel={t("cancel")}
					confirmLabel={t("delete")}
					onClose={(): void => setDeleteDialog(false)}
					onConfirm={deleteSelected}
				/>
			)}

			{extractDialog && (
				<SelectExtractBlueprintDialog
					onClose={(): void => setExtractDialog(false)}
					onConfirm={generateExtracts}
				/>
			)}
		</>
	);
};

export default TrajectoryOverview;
